import { bookingGroupStatus, byRowId, groupRowsByBooking, type BookingRows } from "@bednbody/logic";
import { AdminDataTable, AdminTableIdentity } from "@bednbody/ui/components/admin-data-table";
import { Badge } from "@bednbody/ui/components/badge";
import { Button } from "@bednbody/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@bednbody/ui/components/dropdown-menu";
import { CaretDownIcon, CaretUpIcon, DotsThreeVerticalIcon } from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState, type ReactNode } from "react";
import { bookingsCsv, bookingsCsvName } from "@/features/bookings/bookings.csv";
import { bookingStatusLabel } from "@/lib/bookingStatusLabels";
import type { useOpsBookings } from "../ops-dashboard.hooks";
import { BookingDetailModal } from "./BookingDetailModal";

type Bookings = NonNullable<ReturnType<typeof useOpsBookings>["data"]>;
type Booking = Bookings[number];
type Group = BookingRows<Booking>;

interface BookingsTableProps {
  bookings: Bookings;
  isLoading: boolean;
  header?: ReactNode;
}

function groupStatus(group: Group) {
  return bookingGroupStatus(group.map((booking) => booking.status));
}

/** `null` when this viewer may not see money (0285) — not zero. */
function groupAmount(group: Group): number | null {
  return group.reduce<number | null>(
    (sum, booking) =>
      sum === null || booking.amount_due === null ? null : sum + booking.amount_due,
    0,
  );
}

function serviceLabel(booking: Booking): string {
  const name = booking.service_variants?.services?.name ?? "—";
  const duration = booking.service_variants?.duration_minutes;
  return duration ? `${name} · ${duration} min` : name;
}

function scheduledLabel(value: string): string {
  return new Date(value).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" });
}

export function BookingsTable({ bookings, isLoading, header }: BookingsTableProps) {
  const [detailId, setDetailId] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  // Group before sorting, filtering, or pagination: all clients in one booking stay together.
  const groups = useMemo(
    () => groupRowsByBooking(bookings, (booking) => booking.booking_group_id, byRowId),
    [bookings],
  );

  function toggle(group: Group) {
    const id = group[0].booking_group_id ?? group[0].id;
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const columns: ColumnDef<Group, unknown>[] = [
    {
      id: "ref",
      accessorFn: (group) => group[0].ref,
      header: "Booking",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.length > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={
                expanded.has(row.original[0].booking_group_id ?? row.original[0].id)
                  ? "Collapse booking group"
                  : "Expand booking group"
              }
              aria-expanded={expanded.has(row.original[0].booking_group_id ?? row.original[0].id)}
              onClick={(event) => {
                event.stopPropagation();
                toggle(row.original);
              }}
            >
              {expanded.has(row.original[0].booking_group_id ?? row.original[0].id) ? (
                <CaretUpIcon />
              ) : (
                <CaretDownIcon />
              )}
            </Button>
          ) : null}
          <AdminTableIdentity
            title={row.original[0].ref}
            subtitle={
              row.original.length > 1
                ? `${row.original.length} clients · ${serviceLabel(row.original[0])}`
                : serviceLabel(row.original[0])
            }
            fallback="BK"
          />
        </div>
      ),
    },
    {
      id: "hub",
      accessorFn: (group) => group[0].hubs?.name ?? "",
      header: "Hub",
      cell: ({ row }) => row.original[0].hubs?.name ?? "—",
    },
    {
      id: "service",
      accessorFn: (group) => group.map(serviceLabel).join(", "),
      header: "Service",
      cell: ({ row }) =>
        row.original.length > 1 ? `${row.original.length} services` : serviceLabel(row.original[0]),
    },
    {
      id: "scheduled",
      accessorFn: (group) => group[0].scheduled_at,
      header: "Scheduled",
      cell: ({ row }) => scheduledLabel(row.original[0].scheduled_at),
    },
    {
      id: "payment",
      accessorFn: (group) => group[0].payment_method,
      header: "Payment",
      cell: ({ row }) => row.original[0].payment_method.replaceAll("_", " "),
    },
    {
      id: "amount",
      accessorFn: (group) => groupAmount(group) ?? -1,
      header: "Amount due",
      cell: ({ row }) => {
        const amount = groupAmount(row.original);
        return amount === null ? "—" : `₱${amount.toFixed(2)}`;
      },
    },
    {
      id: "status",
      accessorFn: groupStatus,
      header: "Status",
      cell: ({ row }) => (
        <Badge variant="outline">{bookingStatusLabel(groupStatus(row.original))}</Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      enableSorting: false,
      cell: ({ row }) => (
        <div onClick={(event) => event.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Actions for ${row.original[0].ref}`}
                />
              }
            >
              <DotsThreeVerticalIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {row.original.map((booking, index) => (
                <DropdownMenuItem key={booking.id} onClick={() => setDetailId(booking.id)}>
                  View {row.original.length > 1 ? `client ${index + 1} · ` : ""}
                  {booking.ref}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  function exportBookings(matching: Group[]) {
    const rows = matching.flatMap((group) => group);
    const csv = bookingsCsv(
      rows.map((booking) => ({
        ref: booking.ref,
        hub: booking.hubs?.name ?? "—",
        service: serviceLabel(booking),
        scheduledAt: booking.scheduled_at,
        paymentMethod: booking.payment_method,
        amountDue: booking.amount_due ?? null,
        status: booking.status,
      })),
    );
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = bookingsCsvName();
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  return (
    <>
      <AdminDataTable
        presentation="standalone"
        data={groups}
        columns={columns}
        getRowId={(group) => group[0].booking_group_id ?? group[0].id}
        searchText={(group) =>
          group
            .map((booking) =>
              [
                booking.ref,
                booking.hubs?.name,
                serviceLabel(booking),
                booking.payment_method,
                booking.status,
              ].join(" "),
            )
            .join(" ")
        }
        searchPlaceholder="Search ref, hub, service, or status…"
        facets={[
          {
            id: "status",
            label: "Status",
            options: [
              "pending",
              "awaiting_rider",
              "dispatched",
              "en_route_to_client",
              "arrived",
              "in_session",
              "completed",
              "cancelled",
            ].map((status) => ({ value: status, label: bookingStatusLabel(status) })),
            getValue: groupStatus,
          },
        ]}
        initialSorting={[{ id: "scheduled", desc: true }]}
        onExport={exportBookings}
        exportScopeLabel="latest 200 loaded booking rows"
        loading={isLoading}
        header={header}
        isRowExpanded={(group) => expanded.has(group[0].booking_group_id ?? group[0].id)}
        renderExpandedRow={(group) => (
          <div className="grid gap-1 p-3 pl-12">
            {group.map((booking, index) => (
              <Button
                key={booking.id}
                type="button"
                variant="ghost"
                className="justify-start"
                onClick={() => setDetailId(booking.id)}
              >
                Client {index + 1} · {serviceLabel(booking)} · {bookingStatusLabel(booking.status)}
              </Button>
            ))}
          </div>
        )}
      />
      <BookingDetailModal
        isOpen={detailId !== null}
        onClose={() => setDetailId(null)}
        bookingId={detailId}
      />
    </>
  );
}
